import { useState, useEffect } from "react";

import {
  Apple,
  ArrowRight,
  Book,
  BookMarked,
  BookOpenText,
  ClipboardList,
  Clock,
  ChevronLeft,
  ChevronRight,
  Globe,
  GraduationCap,
  Joystick,
  LaptopMinimal,
  Map,
  Microscope,
  Pencil,
  PersonStanding,
  Pi,
  Play,
  SquareArrowOutUpRight,
  Users,
  File,
  CheckSquare,
  Blocks,
  Archive,
  Layers,
  FileText,
  Files,
  FilesIcon,
  CircleCheck,
  AlertTriangle,
  AlertTriangleIcon
} from "lucide-react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Button, Spinner } from "reactstrap";
import { bindActionCreators } from "redux";

import bannerImage from "./professor.jpeg";
import { logout } from "../../../actions/baseActions";
import URL from "../../../config/api";
import axios from "../../../config/axios";
import { friendlyDate } from "../../../utils/date";
import { formatString } from "../../../utils/string";
import { ContentCard } from "../../ContentCard";

import "./styles.css";

function NewProfessorDashboard({ ...props }) {
  const [contents, setContents] = useState();
  const [activities, setActivities] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerRow, setItemsPerRow] = useState(6);

  useEffect(() => {
    pullDashboardData();
    pullContent();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerRow(2);
      } else if (window.innerWidth < 992) {
        setItemsPerRow(3);
      } else if (window.innerWidth < 1200) {
        setItemsPerRow(4);
      } else {
        setItemsPerRow(6);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once to set initial state

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const pullDashboardData = async () => {
    try {
      const dashboardResp = await axios.get(
        `${URL.api}general/dashboard/user`,
        {
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      const parsedActivities = dashboardResp.data.data.activities.splice(0, 4);
      setActivities(parsedActivities);
    } catch (error) {
      toastr.error("Erro", "Erro ao buscar conteúdos");
    }
  };

  const pullContent = async () => {
    try {
      const gamesObj = {
        ability: "null",
        content: true,
        private: false,
        type: "1"
      };

      const losResp = await axios.post(
        `${URL.api}content/los/listLosFilterMenu?page=1&page_size=12`,
        gamesObj,
        {
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      const contentList = losResp.data.data.reduce((acc, lo) => {
        const typedContent = lo.content.map(content => ({
          ...content,
          type: lo.name,
          action: [
            {
              title: "Abrir conteúdo",
              icon: <SquareArrowOutUpRight size={14} />,
              action: () => {
                props.history.push(`/content/view/${content.id}`);
              }
            }
          ]
        }));

        return [...acc, ...typedContent];
      }, []);

      setContents(contentList);
    } catch (error) {
      toastr.error("Erro", "Erro ao buscar conteúdos");
    }
  };

  const handleActivityIcon = (disciplineName, color = "white", size = 24) => {
    switch (disciplineName) {
      case "Matemática":
        return <Pi size={size} className={`text-${color}`} />;
      case "Literatura":
        return <Book size={size} className={`text-${color}`} />;
      case "Português":
        return <Pencil size={size} className={`text-${color}`} />;
      case "Ciências":
        return <Microscope size={size} className={`text-${color}`} />;
      case "Física":
        return <Apple size={size} className={`text-${color}`} />;
      case "Geografia":
        return <Globe size={size} className={`text-${color}`} />;
      case "História":
        return <BookOpenText size={size} className={`text-${color}`} />;
      case "Educação Física":
        return <PersonStanding size={size} className={`text-${color}`} />;
      case "Religião":
        return <BookMarked size={size} className={`text-${color}`} />;
      case "Tecnologias da Informação e Comunicação":
        return <LaptopMinimal size={size} className={`text-${color}`} />;

      default:
        return <GraduationCap size={size} className={`text-${color}`} />;
    }
  };

  const handleActivityColor = disciplineName => {
    switch (disciplineName) {
      case "Matemática":
        return "info";
      case "Literatura":
        return "warning";
      case "Português":
        return "orange";
      case "Ciências":
        return "lime";
      case "Física":
        return "orange";
      case "Geografia":
        return "danger";
      case "História":
        return "primary";
      case "Educação Física":
        return "warning";
      case "Religião":
        return "danger";
      case "Tecnologias da Informação e Comunicação":
        return "purple";

      default:
        return "info";
    }
  };

  const hasContentImage = content =>
    content.image && content.image.includes(".educandus.com.br/");

  const handleSplicedDate = date => {
    const newDate = friendlyDate(date);
    return newDate.substring(0, newDate.indexOf(", às"));
  };

  const checkIfContentIsNew = content => {
    const today = new Date();
    const contentDate = new Date(content.updated_at);
    const diffTime = Math.abs(today - contentDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays <= 30;
  };

  const handlePrevClick = () => {
    setCurrentIndex(prevIndex => Math.max(prevIndex - itemsPerRow, 0));
  };

  const handleNextClick = () => {
    setCurrentIndex(prevIndex =>
      Math.min(prevIndex + itemsPerRow, contents.length - itemsPerRow)
    );
  };

  return (
    <div className="container">
      <section className="mb-5 d-flex flex-column">
        <h3 className="m-0 font-weight-bold">Olá, {props.user.user.name} 👋</h3>
      </section>
      <section className="mb-5">
        <div className="row g-4">
          <div className="col-lg-6 d-flex">
            <a
              href="/adr/external-login"
              className="w-110 h-110 d-block text-decoration-none rounded-lg shadow-sm overflow-hidden hover-scale position-relative"
              style={{
                backgroundColor: "#fff",
                minHeight: "auto"
              }}
            >
              <img
                src={bannerImage}
                alt="Banner Avaliando Conhecimentos"
                className="w-100 h-100"
                style={{
                  objectFit: "contain",
                  display: "block"
                }}
              />
            </a>
          </div>
          <div className="col-lg-6">
            <div className="row row-cols-1 row-cols-md-2 g-3">
              {/* Card 1: Minhas turmas */}
              <div className="col p-3">
                <a
                  href="/classroom/me"
                  className="card h-100 border-0 shadow-sm modern-shortcut-card text-decoration-none p-2"
                >
                  <div className="card-body d-flex align-items-center">
                    <div
                      className="icon-box rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                      style={{
                        backgroundColor: "#6F42C120",
                        width: "50px",
                        height: "50px"
                      }}
                    >
                      <Users size={24} className="text-purple" />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-dark mb-1">
                        Minhas turmas
                      </h6>
                      <small className="text-muted">Gerencie seus alunos</small>
                    </div>
                  </div>
                </a>
              </div>
              <div className="col">
                <a
                  href="/activity/create"
                  className="card h-100 border-0 shadow-sm modern-shortcut-card text-decoration-none p-2"
                >
                  <div className="card-body d-flex align-items-center">
                    <div
                      className="icon-box rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                      style={{
                        backgroundColor: "#F48C5120",
                        width: "50px",
                        height: "50px"
                      }}
                    >
                      <CircleCheck size={24} className="text-warning" />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-dark mb-1">
                        Criar Atividade
                      </h6>
                      <small className="text-muted">
                        Nova tarefa ou exercício
                      </small>
                    </div>
                  </div>
                </a>
              </div>
              <div className="col">
                <a
                  href="content/list/0"
                  className="card h-100 border-0 shadow-sm modern-shortcut-card text-decoration-none mrt-3"
                >
                  <div className="card-body d-flex align-items-center">
                    <div
                      className="icon-box rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                      style={{
                        backgroundColor: "#97CA5220",
                        width: "50px",
                        height: "50px"
                      }}
                    >
                      <Book size={24} className="text-lime" />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-dark mb-1">
                        Biblioteca
                      </h6>
                      <small className="text-muted">Explore os conteúdos</small>
                    </div>
                  </div>
                </a>
              </div>
              <div className="col p-3">
                <a
                  href="/requests"
                  className="card h-100 border-0 shadow-sm modern-shortcut-card text-decoration-none mrt-3"
                >
                  <div className="card-body d-flex align-items-center">
                    <div
                      className="icon-box rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                      style={{
                        backgroundColor: "#E36767",
                        width: "50px",
                        height: "50px"
                      }}
                    >
                      <AlertTriangle size={24} className="text-danger" />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-dark mb-1">
                        Minhas solicitações
                      </h6>
                      <small className="text-muted">Acesse seu chamados</small>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="d-flex flex-column m-0 mb-5">
        <div className="d-inline-flex justify-content-between">
          <h5 className="font-weight-bold m-0">Conteúdos</h5>
          <div>
            <Button
              onClick={handlePrevClick}
              disabled={currentIndex === 0}
              className="rounded-circle btn-sm bg-white tp-2"
            >
              <ChevronLeft size={18} />
            </Button>
            <Button
              onClick={handleNextClick}
              disabled={
                !contents || currentIndex >= contents.length - itemsPerRow
              }
              className="rounded-circle btn-sm bg-white"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>

        {!contents ? (
          <div
            className="d-flex align-items-center justify-content-center w-100 rounded mt-3 text-muted"
            style={{ minHeight: "200px" }}
          >
            <Spinner color="secondary" size="sm" className="mr-1" />
            Carregando conteúdos...
          </div>
        ) : contents.length === 0 ? (
          <div
            className="d-flex align-items-center justify-content-center w-100 rounded mt-3 text-muted"
            style={{ minHeight: "320px" }}
          >
            <Spinner color="secondary" size="sm" className="mr-1" />
            Não há conteúdos disponíveis no momento.
          </div>
        ) : (
          <div className="row">
            {contents
              .slice(currentIndex, currentIndex + itemsPerRow)
              .map(content => (
                <div className="col-12 col-sm-6 col-lg-4 mb-3" key={content.id}>
                  <ContentCard.Root
                    titleLink={`/content/view/${content.id}?title=${content.name}`}
                  >
                    <ContentCard.Banner
                      fallbackColor={handleActivityColor(
                        content.discipline_name
                      )}
                      fallbackIcon={handleActivityIcon(content.discipline_name)}
                      // actions={content.action}
                      badge={checkIfContentIsNew(content) && "Novo"}
                      image={hasContentImage(content) && content.image}
                      height="108px"
                    />
                    <ContentCard.Main>
                      <ContentCard.Header title={content.name}>
                        {/* <ContentCard.Item>
                          <small>
                            {content.discipline_name} ({content.ability_code})
                          </small>
                        </ContentCard.Item> */}
                      </ContentCard.Header>
                    </ContentCard.Main>
                  </ContentCard.Root>
                </div>
              ))}
          </div>
        )}
      </section>
    </div>
  );
}

const mapStateToProps = state => ({
  user: state.user,
  general: state.general,
  profile: state.profile,
  form: state.form
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      logout
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NewProfessorDashboard);
